import { useEffect, useState, useCallback } from "react";
import { useTheme } from "@/components/ThemeProvider";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sun, Moon, Monitor, RotateCcw, Palette, Plus, Sparkles, Accessibility, Type, LayoutGrid, Wind } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { PresetGallery } from "@/components/appearance/PresetGallery";
import { TypographyPanel } from "@/components/appearance/TypographyPanel";
import { LayoutPanel } from "@/components/appearance/LayoutPanel";
import { AtmospherePanel } from "@/components/appearance/AtmospherePanel";
import { ThemeEditor } from "@/components/appearance/ThemeEditor";
import { ThemePreview } from "@/components/appearance/ThemePreview";
import { GradientEditor } from "@/components/appearance/GradientEditor";
import { ImportExport, exportTheme } from "@/components/appearance/ImportExport";
import { ConfirmDialog } from "@/components/dialogs";

const MODES = [
  { value: "system", label: "Follow Device", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

export default function Appearance() {
  const {
    mode, setMode, reducedMotion, setReducedMotion, themeId,
    selectTheme, resetToDefault, config, setConfig,
  } = useTheme();

  const [themes, setThemes] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [orgThemeId, setOrgThemeId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  // The organisation default theme (what a personal theme "overrides").
  const orgBaseTheme = themes.find((t) => t.id === orgThemeId)
    || themes.find((t) => t.is_preset) || themes[0];

  const load = useCallback(async () => {
    try {
      const [list, a] = await Promise.all([apiFetch("/themes"), apiFetch("/appearance")]);
      setThemes(list);
      setIsAdmin(!!a.is_admin);
      setOrgThemeId(a.org_active_theme_id);
    } catch (e) { toast.error(e.message || "Could not load themes."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onApply = (t) => { selectTheme(t); toast.success(`Applied “${t.name}”`); };
  const onSetOrg = async (t) => {
    try {
      await apiFetch("/appearance/org", { method: "PUT", body: JSON.stringify({ active_theme_id: t.id }) });
      setOrgThemeId(t.id);
      toast.success(`“${t.name}” is now the organisation default`);
    } catch (e) { toast.error(e.message); }
  };
  const onDuplicate = async (t) => {
    try {
      const nt = await apiFetch(`/themes/${t.id}/duplicate`, { method: "POST" });
      await load();
      toast.success(`Duplicated as “${nt.name}”`);
      setEditing(nt);
    } catch (e) { toast.error(e.message); }
  };
  const onArchive = async (t) => {
    try {
      await apiFetch(`/themes/${t.id}/archive`, { method: "PUT", body: JSON.stringify({ archived: true }) });
      await load();
      toast.success(`Archived “${t.name}”`);
    } catch (e) { toast.error(e.message); }
  };
  const onCreate = async () => {
    try {
      const base = themes.find((x) => x.id === (themeId || orgThemeId)) || themes[0];
      const nt = await apiFetch(`/themes/${base.id}/duplicate`, { method: "POST" });
      await load();
      setEditing(nt);
    } catch (e) { toast.error(e.message); }
  };
  const onSave = async ({ id, name, description, config: cfg }) => {
    try {
      const saved = await apiFetch(`/themes/${id}`, {
        method: "PUT", body: JSON.stringify({ name, description, config: cfg }),
      });
      setEditing(null);
      if (id === themeId) { setConfig(saved.config); selectTheme(saved); }
      await load();
      toast.success("Theme saved");
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="animate-fade-up" data-testid="appearance-page">
      <PageHeader eyebrow="Settings" title="Appearance"
        subtitle="Divine cinematic themes, gradients and typography — applied live across ContentOS."
        actions={
          <div className="flex flex-wrap gap-2">
            <ImportExport onImported={() => load()} />
            {isAdmin && (
              <Button className="gap-1.5" onClick={onCreate} data-testid="create-theme">
                <Plus className="h-4 w-4" /> Create theme
              </Button>
            )}
          </div>
        } />

      {/* appearance mode + accessibility */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5 shadow-soft" data-testid="mode-card">
          <h3 className="mb-3 font-heading text-lg font-semibold">Appearance mode</h3>
          <div className="flex gap-2">
            {MODES.map((m) => {
              const Icon = m.icon; const active = mode === m.value;
              return (
                <button key={m.value} onClick={() => setMode(m.value)} data-testid={`mode-${m.value}`}
                  className={cn("flex flex-1 flex-col items-center gap-1.5 rounded-lg border p-3 text-xs font-medium transition-colors",
                    active ? "border-accent bg-accent-subtle text-foreground" : "border-border text-muted-foreground hover:bg-muted/50")}>
                  <Icon className="h-5 w-5" /> {m.label}
                </button>
              );
            })}
          </div>
        </Card>
        <Card className="flex items-center justify-between p-5 shadow-soft" data-testid="motion-card">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><Accessibility className="h-5 w-5" /></span>
            <div>
              <h3 className="font-heading text-lg font-semibold">Reduce motion</h3>
              <p className="text-xs text-muted-foreground">Minimise animations and transitions for a calmer, faster experience.</p>
            </div>
          </div>
          <Switch checked={reducedMotion} onCheckedChange={setReducedMotion} data-testid="reduce-motion-switch" />
        </Card>
      </div>

      <Tabs defaultValue="themes">
        <TabsList data-testid="appearance-tabs">
          <TabsTrigger value="themes" className="gap-1.5" data-testid="tab-themes"><Palette className="h-4 w-4" /> Themes</TabsTrigger>
          <TabsTrigger value="typography" className="gap-1.5" data-testid="tab-typography"><Type className="h-4 w-4" /> Typography</TabsTrigger>
          <TabsTrigger value="layout" className="gap-1.5" data-testid="tab-layout"><LayoutGrid className="h-4 w-4" /> Layout</TabsTrigger>
          <TabsTrigger value="atmosphere" className="gap-1.5" data-testid="tab-atmosphere"><Wind className="h-4 w-4" /> Atmosphere</TabsTrigger>
          <TabsTrigger value="gradients" className="gap-1.5" data-testid="tab-gradients"><Sparkles className="h-4 w-4" /> Gradients</TabsTrigger>
        </TabsList>

        <TabsContent value="themes" className="mt-5">
          <PresetGallery
            themes={themes} activeThemeId={themeId} orgThemeId={orgThemeId} isAdmin={isAdmin}
            onApply={onApply} onSetOrg={onSetOrg} onDuplicate={onDuplicate}
            onEdit={(t) => setEditing(t)} onArchive={onArchive}
            onPreview={(t) => setPreview(t)} onExport={(t) => exportTheme(t.id, t.name)} />
          <div className="mt-5 flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">Personal changes only affect your account. Revert any time.</p>
            <Button variant="outline" className="gap-1.5" onClick={() => setConfirmReset(true)} data-testid="reset-appearance">
              <RotateCcw className="h-4 w-4" /> Reset to organisation default
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="typography" className="mt-5">
          <TypographyPanel isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="layout" className="mt-5">
          <LayoutPanel isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="atmosphere" className="mt-5">
          <AtmospherePanel isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="gradients" className="mt-5">
          <Card className="p-5 shadow-soft">
            <div className="mb-4">
              <h3 className="font-heading text-lg font-semibold">Gradient system</h3>
              <p className="text-xs text-muted-foreground">
                Subtle, cinematic gradients for the current theme. {isAdmin ? "Saved into the active theme." : "Administrators can save gradient changes into themes."}
              </p>
            </div>
            <GradientEditor gradients={config?.gradients}
              onChange={(g) => setConfig({ ...config, gradients: g })} />
            {isAdmin && (
              <div className="mt-4 flex justify-end">
                <Button className="gap-1.5" data-testid="save-gradients"
                  onClick={async () => {
                    const t = themes.find((x) => x.id === themeId);
                    if (!t) return toast.error("Select or apply a theme first.");
                    if (t.is_preset) return toast.error("Duplicate this preset to save gradient changes.");
                    await onSave({ id: t.id, name: t.name, description: t.description, config: { ...t.config, gradients: config.gradients } });
                  }}>
                  Save gradients to theme
                </Button>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {editing && (
        <ThemeEditor open={!!editing} onOpenChange={(o) => !o && setEditing(null)}
          theme={editing} baseConfig={orgBaseTheme?.config} baseName={orgBaseTheme?.name}
          onSave={onSave} onPreview={(cfg) => setPreview({ name: cfg.name, config: cfg })} />
      )}
      {preview && (
        <ThemePreview open={!!preview} onOpenChange={(o) => !o && setPreview(null)}
          config={preview.config} themeName={preview.name}
          onApply={preview.id ? () => { onApply(preview); setPreview(null); } : null} />
      )}
      <ConfirmDialog open={confirmReset} onOpenChange={setConfirmReset}
        title="Reset your appearance to the organisation default?"
        description="This clears your personal theme and colour overrides and returns you to the organisation default. It only affects your account — nobody else is changed."
        confirmLabel="Reset my appearance" variant="destructive"
        onConfirm={async () => { setConfirmReset(false); await resetToDefault(); await load(); toast.success("Reset to organisation default"); }} />
    </div>
  );
}
